import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
  Checkbox,
  Avatar,
  Stack,
  IconButton,
  InputAdornment,
  alpha,
} from '@mui/material';
import { Email, Refresh as RefreshIcon, Search as SearchIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import * as api from '../../services/api';
import EmailTabs from './EmailTabs';
import { useI18n } from '../../i18n/useI18n';

const getSnippet = (html?: string): string => {
  if (!html) return '';
  const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!text) {
    if (/<img[^>]*>/i.test(html)) return 'image';
    return '';
  }
  return text.length > 80 ? `${text.slice(0, 80)}…` : text;
};

const getColorForEmail = (email: string): string => {
  const colors = [
    '#f44336', '#e91e63', '#9c27b0', '#3f51b5', '#2196f3',
    '#009688', '#4caf50', '#ff9800', '#795548', '#607d8b',
  ];
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = email.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const getAvatarTextColor = (bgColor: string): string => {
  const hex = bgColor.replace('#', '');
  if (hex.length !== 6) return '#fff';
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.6 ? '#fff' : '#000';
};

const formatTime = (iso: string): string => {
  const d = new Date(iso);
  const now = new Date();
  const isSameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();

  if (isSameDay) {
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const EmailSent: React.FC = () => {
  const { admin: a, pages: t, formatMessage } = useI18n();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<api.EmailCampaignItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<api.EmailCampaignDetail | null>(null);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());

  const limit = 20;

  const params = useMemo(() => ({
    page,
    limit,
    q: query.trim() ? query.trim() : undefined,
  }), [page, query]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const res = await api.getEmailCampaigns(params);
      setRows(res.data);
      setTotalPages(res.pagination.totalPages || 1);
    } catch (err: any) {
      console.error('Failed to fetch email logs:', err);
      toast.error(err.response?.data?.error || 'Không thể tải danh sách email đã gửi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const openDetail = async (id: string) => {
    try {
      setSelectedId(id);
      setDetailLoading(true);
      const res = await api.getEmailCampaignDetail(id);
      setDetail(res);
    } catch (err: any) {
      console.error('Failed to fetch email log detail:', err);
      toast.error(err.response?.data?.error || 'Không thể tải chi tiết email');
      setSelectedId(null);
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
  };

  const toggleRowSelected = (id: string) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allChecked = rows.length > 0 && selectedRowIds.size === rows.length;
  const someChecked = selectedRowIds.size > 0 && selectedRowIds.size < rows.length;

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 1.5, sm: 3 },
        px: { xs: 1.5, sm: 3 },
        '@keyframes emSoftIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Box
        sx={{
          mb: { xs: 1.5, sm: 2 },
          p: { xs: 1.75, sm: 2.5 },
          borderRadius: { xs: 3, sm: 4 },
          border: '1px solid',
          borderColor: alpha('#f48fb1', 0.25),
          background: `linear-gradient(135deg, ${alpha('#fff', 0.95)} 0%, ${alpha('#ffc1e3', 0.35)} 100%)`,
          boxShadow: `0 10px 28px ${alpha('#bf5f82', 0.1)}`,
          animation: 'emSoftIn 0.45s ease both',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha('#f48fb1', 0.2),
              color: 'primary.dark',
              flexShrink: 0,
            }}
          >
            <Email />
          </Box>
          <Typography
            component="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.35rem', sm: '1.85rem' },
              letterSpacing: '-0.02em',
              color: '#2d2430',
            }}
          >
            {a.email.title}
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ animation: 'emSoftIn 0.45s ease both', animationDelay: '0.05s' }}>
        <EmailTabs />
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ mb: 2, animation: 'emSoftIn 0.45s ease both', animationDelay: '0.1s' }}
      >
        <TextField
          placeholder={a.email.searchPlaceholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(1);
          }}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 999,
              bgcolor: '#fff',
            },
          }}
        />
        <Stack direction="row" spacing={1}>
          <IconButton
            onClick={fetchLogs}
            disabled={loading}
            sx={{
              border: '1px solid',
              borderColor: alpha('#f48fb1', 0.35),
              bgcolor: '#fff',
              color: 'primary.dark',
            }}
          >
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            color="error"
            disabled={selectedRowIds.size === 0 || loading}
            onClick={async () => {
              if (selectedRowIds.size === 0) return;
              if (!window.confirm(formatMessage(a.email.deleteConfirm, { n: selectedRowIds.size }))) return;
              try {
                const ids = Array.from(selectedRowIds);
                const res = await api.deleteEmailCampaigns(ids);
                toast.success(res.message || `Đã xóa ${res.deletedCount} email`);
                setSelectedRowIds(new Set());
                fetchLogs();
              } catch (err: any) {
                console.error('Failed to delete campaigns:', err);
                toast.error(err.response?.data?.error || 'Không thể xóa email đã gửi');
              }
            }}
            sx={{
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 700,
              px: 2,
              whiteSpace: 'nowrap',
            }}
          >
            {a.email.deleteSelected}
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ display: { xs: 'none', md: 'block' }, animation: 'emSoftIn 0.45s ease both', animationDelay: '0.14s' }}>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allChecked}
                    indeterminate={someChecked}
                    onChange={() => {
                      if (allChecked) setSelectedRowIds(new Set());
                      else setSelectedRowIds(new Set(rows.map((r) => r._id)));
                    }}
                  />
                </TableCell>
                <TableCell>{a.email.colSubject}</TableCell>
                <TableCell>{a.email.colPreview}</TableCell>
                <TableCell align="right">{a.email.colRecipients}</TableCell>
                <TableCell align="right">{a.email.colStats}</TableCell>
                <TableCell align="right">{a.email.colTime}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">{a.email.empty}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow
                    key={r._id}
                    hover
                    selected={selectedRowIds.has(r._id)}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openDetail(r._id)}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedRowIds.has(r._id)}
                        onChange={() => toggleRowSelected(r._id)}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 520 }}>
                      <Typography variant="body2" noWrap title={r.subject} sx={{ fontWeight: 600 }}>
                        {r.subject}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 420 }}>
                      <Typography variant="body2" noWrap color="text.secondary" title={getSnippet(r.html)}>
                        {getSnippet(r.html)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">{r.stats.total}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={formatMessage(a.email.statsChip, {
                          sent: r.stats.sent,
                          failed: r.stats.failed,
                        })}
                        color={r.stats.failed > 0 ? 'warning' : 'success'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {formatTime(r.createdAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={{ display: { xs: 'block', md: 'none' }, animation: 'emSoftIn 0.45s ease both', animationDelay: '0.14s' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
            {a.email.empty}
          </Typography>
        ) : (
          rows.map((r, index) => (
            <Paper
              key={r._id}
              elevation={0}
              sx={{
                mb: 1.25,
                p: 1.5,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                animation: 'emSoftIn 0.4s ease both',
                animationDelay: `${0.16 + Math.min(index, 10) * 0.04}s`,
                '&:active': { bgcolor: alpha('#f48fb1', 0.06) },
              }}
              onClick={() => openDetail(r._id)}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                <Checkbox
                  size="small"
                  checked={selectedRowIds.has(r._id)}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleRowSelected(r._id);
                  }}
                  sx={{ mt: -0.5, ml: -0.75 }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                    <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap title={r.subject}>
                      {r.subject}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontWeight: 600 }}>
                      {formatTime(r.createdAt)}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    sx={{ mt: 0.35, fontSize: '0.8rem' }}
                    title={getSnippet(r.html)}
                  >
                    {getSnippet(r.html)}
                  </Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatMessage(a.email.recipientsCount, { n: r.stats.total })}
                    </Typography>
                    <Chip
                      size="small"
                      label={formatMessage(a.email.statsChip, {
                        sent: r.stats.sent,
                        failed: r.stats.failed,
                      })}
                      color={r.stats.failed > 0 ? 'warning' : 'success'}
                      variant="outlined"
                      sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700 }}
                    />
                  </Stack>
                </Box>
              </Box>
            </Paper>
          ))
        )}
      </Box>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          {formatMessage(a.email.pageOf, { page, totalPages })}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          >
            {a.email.prev}
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          >
            {a.email.next}
          </Button>
        </Stack>
      </Stack>

      <Dialog open={!!selectedId} onClose={closeDetail} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{a.email.detailTitle}</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : detail ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2">
                <strong>Recipients:</strong> {detail.stats.total} (SENT: {detail.stats.sent}, FAILED: {detail.stats.failed})
              </Typography>
              <Typography variant="body2">
                <strong>Subject:</strong> {detail.subject}
              </Typography>
              <Typography variant="subtitle2">Recipients</Typography>
              <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 180, overflow: 'auto', borderRadius: 2.5 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {detail.recipients?.map((r) => (
                    <Chip
                      key={`${r.email}-${r.status}`}
                      size="small"
                      label={r.email}
                      color={r.status === 'SENT' ? 'success' : 'error'}
                      variant="outlined"
                      title={r.errorMessage || ''}
                      avatar={
                        <Avatar
                          sx={{
                            bgcolor: getColorForEmail(r.email),
                            width: 22,
                            height: 22,
                            fontSize: 12,
                            color: getAvatarTextColor(getColorForEmail(r.email)),
                          }}
                        >
                          {r.email.charAt(0).toUpperCase()}
                        </Avatar>
                      }
                    />
                  ))}
                </Box>
              </Paper>
              <Divider />
              <Typography variant="subtitle2">HTML Preview</Typography>
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 420, overflow: 'auto', borderRadius: 2.5 }}>
                <div dangerouslySetInnerHTML={{ __html: detail.html }} />
              </Paper>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {a.email.noData || a.email.empty}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={closeDetail} sx={{ textTransform: 'none', fontWeight: 700 }}>
            {t.common.close}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EmailSent;
